/**
 * Tiny hash router: `#/path/segments?query`. Routes are matched in order; `:name` captures.
 */
import { useEffect, useState } from 'react';

export interface Location {
  path: string;
  query: URLSearchParams;
}

export function parseHash(hash: string): Location {
  const raw = hash.replace(/^#/, '') || '/';
  const [path = '/', qs = ''] = raw.split('?');
  return { path: path.startsWith('/') ? path : '/' + path, query: new URLSearchParams(qs) };
}

// ---------- leave guard (ERR-01) ----------

/**
 * Pages with unsaved in-memory work (a catalog draft, the TOR wizard) register a guard; any
 * in-app navigation to another path — a menu link, navigate(), the back button — asks first.
 * The check runs in the capture phase of `hashchange`, before any useLocation listener, so a
 * cancelled navigation never unmounts the page: the previous URL is put back in place.
 */
interface LeaveGuard {
  path: string;
  message: string;
}
const leaveGuards = new Set<LeaveGuard>();
let currentHash = typeof window === 'undefined' ? '' : window.location.hash;
let confirmLeave: (message: string) => boolean = (message) => window.confirm(message);

/** Tests replace the confirmation (window.confirm) with a stub. */
export function setLeaveConfirm(fn: (message: string) => boolean): void {
  confirmLeave = fn;
}

export function addLeaveGuard(path: string, message: string): () => void {
  const g = { path, message };
  leaveGuards.add(g);
  return () => leaveGuards.delete(g);
}

/** Whether leaving `from` for `to` needs (and gets) the user's go-ahead. */
export function mayLeave(fromHash: string, toHash: string): boolean {
  if (!leaveGuards.size) return true;
  const from = parseHash(fromHash).path;
  const to = parseHash(toHash).path;
  if (from === to) return true; // tabs and query changes stay on the page
  const guard = [...leaveGuards].find((g) => g.path === from);
  return !guard || confirmLeave(guard.message);
}

if (typeof window !== 'undefined') {
  window.addEventListener(
    'hashchange',
    (e) => {
      const next = window.location.hash;
      if (!mayLeave(currentHash, next)) {
        e.stopImmediatePropagation();
        window.history.replaceState(null, '', window.location.href.replace(/#.*$/, '') + (currentHash || '#/'));
        return;
      }
      currentHash = next;
    },
    { capture: true },
  );
}

export function useLocation(): Location {
  const [loc, setLoc] = useState(() => parseHash(window.location.hash));
  useEffect(() => {
    const onChange = () => setLoc(parseHash(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return loc;
}

export function navigate(to: string, opts: { replace?: boolean; force?: boolean } = {}): void {
  const hash = to.startsWith('#') ? to : '#' + to;
  // After a successful save the page navigates on purpose: skip the leave guard.
  if (opts.force) currentHash = hash;
  if (opts.replace) {
    const url = window.location.href.replace(/#.*$/, '') + hash;
    window.history.replaceState(null, '', url);
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  } else {
    window.location.hash = hash;
  }
}

/** Match `pattern` like `/d/:designId` against `path`. */
export function matchRoute(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const s = path.split('/').filter(Boolean);
  if (p.length !== s.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!;
    const val = s[i]!;
    if (seg.startsWith(':')) params[seg.slice(1)] = decodeURIComponent(val);
    else if (seg !== val) return null;
  }
  return params;
}

export const href = (path: string) => '#' + path;

/** Ask before leaving this page for another one while `active` (unsaved in-memory work). */
export function useLeaveGuard(active: boolean, message = 'มีการแก้ไขที่ยังไม่บันทึก — ออกจากหน้านี้แล้วการแก้ไขจะหายไป ต้องการออกหรือไม่?'): void {
  useEffect(() => {
    if (!active) return;
    return addLeaveGuard(parseHash(window.location.hash).path, message);
  }, [active, message]);
}
